import React, { useRef, useState } from "react";
import { Upload, X, Star, ImagePlus, Loader2, GripVertical, Play } from "lucide-react";
import api, { resolveImage, apiErrorMsg } from "../lib/api";
import { toast } from "sonner";

/**
 * v2.39 — MediaUploader
 * Premium drag-drop image + video uploader for property/project forms.
 *
 * Features:
 *  - Native HTML5 drag-drop (no library) — desktop + mobile gallery
 *  - Multi-file upload with per-file progress
 *  - Reorder via up/down arrows + drag-handle (mobile-friendly)
 *  - First image is the "Featured / Cover" — Star icon shown on it
 *  - Tap any image to promote to featured (move-to-front)
 *  - Image: 10 MB cap; Video: 50 MB cap (server enforced too)
 *  - Camera capture supported on mobile via accept + capture attribute
 */

const IMG_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
const VID_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];

export default function MediaUploader({
  images = [],
  videos = [],
  onImagesChange,
  onVideosChange,
  imageMaxMB = 10,
  videoMaxMB = 50,
  testidPrefix = "uf",
}) {
  const imgInputRef = useRef();
  const vidInputRef = useRef();
  const cameraInputRef = useRef();
  const [dragActive, setDragActive] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(0);
  const [uploadingVid, setUploadingVid] = useState(0);

  const isVideoFile = (f) => VID_TYPES.includes(f.type) || /\.(mp4|mov|webm|m4v)$/i.test(f.name);
  const isImageFile = (f) => IMG_TYPES.includes(f.type) || /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(f.name);

  const uploadOne = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: kind === "video" ? 240000 : 60000,
    });
    return r.data.url;
  };

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const imgs = files.filter(isImageFile);
    const vids = files.filter(isVideoFile);

    // Validate sizes client-side (server validates too — defence in depth)
    const tooBigImg = imgs.find((f) => f.size > imageMaxMB * 1024 * 1024);
    if (tooBigImg) toast.error(`${tooBigImg.name} > ${imageMaxMB}MB — skipped`);
    const tooBigVid = vids.find((f) => f.size > videoMaxMB * 1024 * 1024);
    if (tooBigVid) toast.error(`${tooBigVid.name} > ${videoMaxMB}MB — skipped`);

    const okImgs = imgs.filter((f) => f.size <= imageMaxMB * 1024 * 1024);
    const okVids = vids.filter((f) => f.size <= videoMaxMB * 1024 * 1024);

    const skipped = files.length - okImgs.length - okVids.length;
    if (skipped) toast.error(`${skipped} file(s) unsupported or too large — skipped`);

    setUploadingImg((n) => n + okImgs.length);
    setUploadingVid((n) => n + okVids.length);

    // Parallel uploads but bounded — concurrency 3 to keep mobile networks happy
    const queue = [...okImgs.map((f) => ({ f, k: "image" })), ...okVids.map((f) => ({ f, k: "video" }))];
    const newImages = [];
    const newVideos = [];
    const concurrency = 3;
    let i = 0;

    const worker = async () => {
      while (i < queue.length) {
        const idx = i++;
        const { f, k } = queue[idx];
        try {
          const url = await uploadOne(f, k);
          if (k === "image") newImages.push(url);
          else newVideos.push(url);
        } catch (err) {
          toast.error(apiErrorMsg(err, `Failed: ${f.name}`));
        } finally {
          if (k === "image") setUploadingImg((n) => Math.max(0, n - 1));
          else setUploadingVid((n) => Math.max(0, n - 1));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));

    if (newImages.length) {
      onImagesChange?.([...(images || []), ...newImages]);
      toast.success(`${newImages.length} image${newImages.length > 1 ? "s" : ""} uploaded`);
    }
    if (newVideos.length) {
      onVideosChange?.([...(videos || []), ...newVideos]);
      toast.success(`${newVideos.length} video${newVideos.length > 1 ? "s" : ""} uploaded`);
    }
  };

  // ----- Drag & drop ---------------------------------------------------
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = () => setDragActive(false);

  // ----- Reorder helpers -----------------------------------------------
  const moveImg = (i, dir) => {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onImagesChange?.(next);
  };
  const setFeatured = (i) => {
    if (i === 0) return;
    const next = [...images];
    const [picked] = next.splice(i, 1);
    next.unshift(picked);
    onImagesChange?.(next);
  };
  const removeImg = (i) => onImagesChange?.(images.filter((_, idx) => idx !== i));
  const removeVid = (i) => onVideosChange?.(videos.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-media`}>
      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`relative rounded-2xl border-2 border-dashed transition-colors p-6 text-center ${
          dragActive
            ? "border-[#0055FF] bg-[#EEF4FF]"
            : "border-zinc-200 bg-zinc-50/60 hover:border-[#0055FF]/40 hover:bg-zinc-50"
        }`}
        data-testid={`${testidPrefix}-dropzone`}
      >
        <input ref={imgInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid={`${testidPrefix}-img-input`} />
        <input ref={vidInputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid={`${testidPrefix}-vid-input`} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid={`${testidPrefix}-cam-input`} />

        <div className="w-12 h-12 mx-auto rounded-full bg-[#EEF4FF] text-[#0055FF] flex items-center justify-center mb-3">
          <Upload size={22} />
        </div>
        <div className="font-bold text-zinc-900 text-[14px]">Drag &amp; drop photos or videos</div>
        <div className="text-[12px] text-zinc-500 mt-1">
          JPG / PNG / WebP up to {imageMaxMB} MB · MP4 / MOV / WebM up to {videoMaxMB} MB
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
          <button type="button" onClick={() => imgInputRef.current?.click()} className="h-9 px-3.5 inline-flex items-center gap-1.5 rounded-lg btn-gradient text-[12px] font-bold" data-testid={`${testidPrefix}-pick-images`}>
            <ImagePlus size={13} /> Add Photos
          </button>
          <button type="button" onClick={() => vidInputRef.current?.click()} className="h-9 px-3.5 inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white text-[12px] font-bold text-zinc-700 hover:border-[#0055FF] hover:text-[#0055FF]" data-testid={`${testidPrefix}-pick-videos`}>
            <Play size={13} /> Add Videos
          </button>
          <button type="button" onClick={() => cameraInputRef.current?.click()} className="md:hidden h-9 px-3.5 inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white text-[12px] font-bold text-zinc-700 hover:border-[#0055FF] hover:text-[#0055FF]" data-testid={`${testidPrefix}-take-photo`}>
            📷 Take Photo
          </button>
        </div>

        {(uploadingImg > 0 || uploadingVid > 0) && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-zinc-200 rounded-full text-[11px] font-bold text-zinc-700" data-testid={`${testidPrefix}-uploading-banner`}>
            <Loader2 size={12} className="animate-spin text-[#0055FF]" />
            Uploading {uploadingImg + uploadingVid} file{(uploadingImg + uploadingVid) > 1 ? "s" : ""}…
          </div>
        )}
      </div>

      {/* Image grid */}
      {images.length > 0 && (
        <div data-testid={`${testidPrefix}-img-grid`}>
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-zinc-500 mb-2 inline-flex items-center gap-1.5">
            <ImagePlus size={11} /> Photos ({images.length})
            <span className="text-[10px] font-normal text-zinc-400 normal-case tracking-normal">— first photo is the cover; tap ⭐ to feature</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {images.map((src, i) => (
              <div key={`${src}-${i}`} className="group relative aspect-square rounded-xl border border-zinc-200 overflow-hidden bg-zinc-50">
                <img src={resolveImage(src)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                {i === 0 && (
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-[#0055FF] to-[#2D7BFF] text-white text-[9px] uppercase tracking-wider font-bold rounded-full shadow-sm">
                    <Star size={9} fill="currentColor" /> Cover
                  </span>
                )}
                {/* Action overlay (visible on hover desktop, always-tap on mobile) */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1.5 gap-1">
                  {i !== 0 && (
                    <button type="button" onClick={() => setFeatured(i)} title="Make cover photo" className="w-7 h-7 rounded-md bg-white text-amber-500 hover:bg-amber-50 flex items-center justify-center" data-testid={`${testidPrefix}-img-feature-${i}`}>
                      <Star size={13} />
                    </button>
                  )}
                  <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} title="Move up" className="w-7 h-7 rounded-md bg-white text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 flex items-center justify-center" data-testid={`${testidPrefix}-img-up-${i}`}>
                    <GripVertical size={13} className="-rotate-90" />
                  </button>
                  <button type="button" onClick={() => moveImg(i, 1)} disabled={i === images.length - 1} title="Move down" className="w-7 h-7 rounded-md bg-white text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 flex items-center justify-center" data-testid={`${testidPrefix}-img-dn-${i}`}>
                    <GripVertical size={13} className="rotate-90" />
                  </button>
                  <button type="button" onClick={() => removeImg(i)} title="Remove" className="w-7 h-7 rounded-md bg-red-50 text-red-700 hover:bg-red-100 flex items-center justify-center ml-auto" data-testid={`${testidPrefix}-img-remove-${i}`}>
                    <X size={13} />
                  </button>
                </div>
                {/* Mobile fallback — always-visible delete corner button */}
                <button type="button" onClick={() => removeImg(i)} className="md:hidden absolute top-1 right-1 w-6 h-6 rounded-full bg-black/65 text-white flex items-center justify-center" aria-label="Remove">
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video grid */}
      {videos.length > 0 && (
        <div data-testid={`${testidPrefix}-vid-grid`}>
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-zinc-500 mb-2 inline-flex items-center gap-1.5">
            <Play size={11} /> Videos ({videos.length})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {videos.map((src, i) => (
              <div key={`${src}-${i}`} className="relative aspect-video rounded-xl border border-zinc-200 overflow-hidden bg-black">
                <video src={resolveImage(src)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-9 h-9 rounded-full bg-white/85 flex items-center justify-center">
                    <Play size={14} className="text-zinc-900 ml-0.5" fill="currentColor" />
                  </div>
                </div>
                <button type="button" onClick={() => removeVid(i)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/65 text-white flex items-center justify-center hover:bg-red-600" aria-label="Remove video" data-testid={`${testidPrefix}-vid-remove-${i}`}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
